import { useState } from 'react';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { User } from 'models/user.model';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import {
  apiProps,
  userAuthenticationStatus,
} from 'enums/AuthenticationServicesEnums';
import { initSocket } from 'shared/utils/initSocket';
import { notification } from 'antd';
import { useUserContext } from 'context/UserContext';
import { AuthContext } from 'context/AuthContext';
import { useLocationsDataContext } from 'context/LocationsDataContext';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import useRedirect from 'shared/utils/useRedirect';
import { CompanySetUpTabHash } from 'enums/CompanySetupEnums';

const { DATA, TOKEN, USER, USER_DETAILS } = apiProps;

interface ParsedUserInterface {
  status: string;
}

const AuthenticationService = () => {
  const navigate = useNavigate();
  const { redirectToCompanyDashboard, redirectToCompanySetUp } = useRedirect();
  const { fetchParentOrganization } = ParentCompanySetupService();
  const { setCompanyImageUrl } = useUserContext();
  const [user, setUser] = useState<User | null>();
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [setNewPasswordSuccess, setSetNewPasswordSuccess] =
    useState<string>('');
  const { setAuthenticated } = AuthContext();

  const { setLocationTableData } = useLocationsDataContext();

  /* Login */
  const adminLogin = async (payload: { email: string; password: string }): Promise<{ requiresOTP: boolean; email: string } | void> => {
    try {
      if (!payload.email) return;
      if (!payload.password) return;
      setButtonLoading(true);
      setCompanyImageUrl(undefined);
      const res = await axiosInstance.post(ApiRoutes.USER_LOGIN, payload);

      // Check if MFA is required (OTP sent message)
      if (res.data.message === 'OTP successfully sent to your email.') {
        setButtonLoading(false);
        return { requiresOTP: true, email: payload.email };
      }

      // MFA not required - proceed with normal login
      localStorage.setItem(USER, res.data[DATA][TOKEN]);
      localStorage.setItem(USER_DETAILS, JSON.stringify(res.data[DATA][USER]));
      setAuthenticated(res.data[DATA][USER]);

      // Initializing socket connection on Logging in
      initSocket(res.data[DATA][TOKEN]);

      let parsedUser: ParsedUserInterface = res.data.data.user;

      if (parsedUser.status === userAuthenticationStatus.VERIFICATION_PENDING) {
        navigate(AppRoutes.ADMIN_INFO);
      } else if (
        parsedUser.status === userAuthenticationStatus.COMPANY_INFO_PENDING
      ) {
        navigate(AppRoutes.COMPANY_DETAILS);
      } else if (
        parsedUser.status === userAuthenticationStatus.COMPANY_ADDRESS_PENDING
      ) {
        navigate(AppRoutes.COMPANY_ADDRESS);
      } else if (
        parsedUser.status === userAuthenticationStatus.PAYMENT_PENDING
      ) {
        navigate(AppRoutes.PLAN_DETAILS);
      } else if (parsedUser.status === userAuthenticationStatus.ACTIVE) {
        const parentData = await fetchParentOrganization();
        if (parentData?.company?.hasLocations) redirectToCompanyDashboard();
        else redirectToCompanySetUp(CompanySetUpTabHash.LOCATIONS)();
      }
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    } finally {
      setButtonLoading(false);
    }
  };

  /* Send Verify Email*/
  const sendVerificationEmail = async (payload: {
    email: string;
    name: string;
  }) => {
    try {
      if (!payload.email) return;
      setButtonLoading(true);
      await axiosInstance.post(ApiRoutes.SEND_VERIFY_EMAIL, payload);
      notification.success({
        message: 'OTP successfully sent to your email',
      });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /* VerifyOTP - ForgotPassword*/
  const verifyOTPForgotPass = async (
    email: string,
    payload: {
      email: string | unknown;
      otp: number;
    }
  ) => {
    try {
      if (!payload.email) return;
      if (!payload.otp) return;
      setButtonLoading(true);
      const res = await axiosInstance.post(ApiRoutes.VERIFY_OTP, payload);
      localStorage.setItem(USER, res.data.data.token);
      navigate(AppRoutes.SET_NEW_PASS, { state: email });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /* Verify OTP */
  const verifyOTP = async (
    forgotPass: boolean | undefined,
    payload: {
      email: string | unknown;
      otp: number;
    }
  ) => {
    try {
      if (!payload.email) return;
      if (!payload.otp) return;
      setButtonLoading(true);
      await axiosInstance.post(ApiRoutes.VERIFY_OTP, payload);
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /* Verify OTP for Login (MFA) */
  const verifyOTPForLogin = async (payload: {
    email: string;
    otp: number;
  }) => {
    try {
      if (!payload.email) return;
      if (!payload.otp) return;
      setButtonLoading(true);
      setCompanyImageUrl(undefined);
      const res = await axiosInstance.post(ApiRoutes.VERIFY_OTP, payload);

      // Store token
      localStorage.setItem(USER, res.data.data.token);

      // Fetch user details to get status
      const userResponse = await axiosInstance.get(ApiRoutes.FETCH_ACCOUNTS);
      const users = userResponse.data.data;
      const currentUser = users.find((u: User) => u.email === payload.email);

      if (currentUser) {
        // Store user details
        localStorage.setItem(USER_DETAILS, JSON.stringify(currentUser));
        setAuthenticated(currentUser);

        // Initializing socket connection
        initSocket(res.data.data.token);

        // Navigate based on user status
        let parsedUser: ParsedUserInterface = currentUser;

        if (parsedUser.status === userAuthenticationStatus.VERIFICATION_PENDING) {
          navigate(AppRoutes.ADMIN_INFO);
        } else if (
          parsedUser.status === userAuthenticationStatus.COMPANY_INFO_PENDING
        ) {
          navigate(AppRoutes.COMPANY_DETAILS);
        } else if (
          parsedUser.status === userAuthenticationStatus.COMPANY_ADDRESS_PENDING
        ) {
          navigate(AppRoutes.COMPANY_ADDRESS);
        } else if (
          parsedUser.status === userAuthenticationStatus.PAYMENT_PENDING
        ) {
          navigate(AppRoutes.PLAN_DETAILS);
        } else if (parsedUser.status === userAuthenticationStatus.ACTIVE) {
          const parentData = await fetchParentOrganization();
          if (parentData?.company?.hasLocations) redirectToCompanyDashboard();
          else redirectToCompanySetUp(CompanySetUpTabHash.LOCATIONS)();
        }
      }
    } catch (error) {
      console.error('Error during OTP verification:', error);
      throw error;
    } finally {
      setButtonLoading(false);
    }
  };

  /* Forgot Password */
  const forgotPassword = async (payload: { email: string }) => {
    try {
      if (!payload.email) return;
      setButtonLoading(true);
      const response = await axiosInstance.put(
        ApiRoutes.SEND_FORGOT_PASS_MAIL,
        payload
      );
      notification.success({
        message: response.data['message'],
      });
      navigate(AppRoutes.FORGOT_PASS_VERIFICATION, { state: payload.email });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /* Mock Payment */
  const mockPayment = async () => {
    try {
      setButtonLoading(true);
      await axiosInstance.get(ApiRoutes.MOCK_PAYMENT);
      navigate(AppRoutes.DASHBOARD);
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /** Set new password */
  const setNewPassword = async (payload: {
    email: string;
    password: string;
  }) => {
    try {
      if (!payload.email) return;
      if (!payload.password) return;
      setButtonLoading(true);
      await axiosInstance.put(ApiRoutes.SET_NEW_PASS, payload);
      setSetNewPasswordSuccess('New password created successfully!');
      navigate(AppRoutes.LOGIN_VIEW);
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /* Logout*/
  const Logout = async () => {
    try {
      localStorage.clear();
      // clear everything in table context
      setLocationTableData(null);
      setCompanyImageUrl(undefined);
      navigate(AppRoutes.LOGIN_VIEW);
    } catch (error) {
    } finally {
    }
  };

  /** Email already taken Check*/
  const emailExists = async (email: string) => {
    try {
      const response = await axiosInstance.post(ApiRoutes.CHECK_EMAIL_EXISTS, {
        email,
      });
      return response.data.exists;
    } catch (error) {
      console.log('Error: ', error);
      return false;
    }
  };

  return {
    user,
    buttonLoading,
    setNewPasswordSuccess,
    adminLogin,
    sendVerificationEmail,
    verifyOTP,
    verifyOTPForgotPass,
    verifyOTPForLogin,
    forgotPassword,
    mockPayment,
    setNewPassword,
    Logout,
    emailExists,
  };
};

export default AuthenticationService;
